import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import {
  ProjectSettings as ProjectSettingsType,
  ResearcherAccessMode,
  MetadataScope,
} from "../types/api";

interface ProjectSettingsProps {
  projectId: number;
  initialTitle: string;
  initialDescription: string;
  // Called after the title/description is saved so the parent can refresh.
  onProjectUpdated?: () => void;
  // Self-unassignment ("Leave Project"). Only shown to PIs who are members of
  // this project — managers view settings but are not members, so they omit it.
  canLeave?: boolean;
  isOnlyPI?: boolean; // The project must keep one PI, so the last PI can't leave.
  leaving?: boolean;
  onLeaveProject?: () => void;
  // Archiving lives here rather than in the project lists: it is a rare,
  // whole-project action, so it belongs behind Settings with a confirm step.
  canArchive?: boolean;
  isArchived?: boolean;
  // Archived projects are read-only server-side: everything but the restore
  // control is disabled rather than offered and refused.
  readOnly?: boolean;
  archiving?: boolean;
  onSetArchived?: (archived: boolean) => void;
}

// A small, dependency-free toggle switch styled with Tailwind.
const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
      checked ? "bg-teal-600" : "bg-gray-300"
    } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const SettingRow: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <div className="flex items-start justify-between gap-6 py-4 border-b border-gray-100 last:border-b-0">
    <div className="max-w-lg">
      <div className="font-medium text-gray-900">{title}</div>
      <p className="text-sm text-gray-500 mt-0.5">{description}</p>
    </div>
    <div className="flex-shrink-0 pt-1">{children}</div>
  </div>
);

export const ProjectSettings: React.FC<ProjectSettingsProps> = ({
  projectId,
  initialTitle,
  initialDescription,
  onProjectUpdated,
  canLeave = false,
  isOnlyPI = false,
  leaving = false,
  onLeaveProject,
  canArchive = false,
  isArchived = false,
  readOnly = false,
  archiving = false,
  onSetArchived,
}) => {
  const { t } = useTranslation(["projectSettings", "common"]);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [settings, setSettings] = useState<ProjectSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Project details (title/description) — saved via a separate endpoint.
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsSuccess, setDetailsSuccess] = useState<string | null>(null);

  const detailsChanged =
    title !== initialTitle || description !== initialDescription;
  const detailsLocked = savingDetails || readOnly;

  useEffect(() => {
    loadSettings();
  }, [projectId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getProjectSettings(projectId);
      setSettings(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("errors.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  // Optimistically update local state; persistence happens on Save.
  const patch = (changes: Partial<ProjectSettingsType>) => {
    setSettings((prev) => (prev ? { ...prev, ...changes } : prev));
    setSuccessMsg(null);
  };

  const handleSave = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      setError(null);
      setSuccessMsg(null);
      const updated = await apiService.updateProjectSettings(projectId, {
        default_researcher_access: settings.default_researcher_access,
        default_metadata_scope: settings.default_metadata_scope,
        auto_notify_on_upload: settings.auto_notify_on_upload,
      });
      setSettings(updated);
      setSuccessMsg(t("settings.saved"));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("errors.saveSettingsFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDetails = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setDetailsError(t("errors.titleRequired"));
      return;
    }
    try {
      setSavingDetails(true);
      setDetailsError(null);
      setDetailsSuccess(null);
      await apiService.adminUpdateProject(projectId, {
        title: trimmedTitle,
        description: description.trim(),
      });
      setDetailsSuccess(t("details.saved"));
      onProjectUpdated?.();
    } catch (err) {
      setDetailsError(
        err instanceof Error ? err.message : t("errors.saveDetailsFailed"),
      );
    } finally {
      setSavingDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
        {t("loading")}
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
        {error}
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="max-w-3xl space-y-6">
      {readOnly && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 text-sm text-gray-600">
          {t("archivedBanner")}
        </div>
      )}

      {/* Project details (title / description) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("details.title")}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {t("details.description")}
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("details.titleLabel")}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setDetailsSuccess(null);
              }}
              disabled={detailsLocked}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("common:fields.description")}
            </label>
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setDetailsSuccess(null);
              }}
              disabled={detailsLocked}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-50"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm">
            {detailsError && (
              <span className="text-red-600">{detailsError}</span>
            )}
            {detailsSuccess && (
              <span className="text-green-600">{detailsSuccess}</span>
            )}
          </div>
          <button
            onClick={handleSaveDetails}
            disabled={detailsLocked || !detailsChanged}
            className="btn btn-primary"
          >
            {savingDetails ? t("details.saving") : t("details.saveButton")}
          </button>
        </div>
      </div>

      {/* Behavioral settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("settings.title")}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {t("settings.description")}
          </p>
        </div>

        <div className="px-6">
          <SettingRow
            title={t("researcherAccess.title")}
            description={t("researcherAccess.description")}
          >
            <select
              value={settings.default_researcher_access}
              onChange={(e) =>
                patch({
                  default_researcher_access: e.target
                    .value as ResearcherAccessMode,
                })
              }
              disabled={readOnly}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="pi_approval">
                {t("researcherAccess.piApproval")}
              </option>
              <option value="auto_grant">
                {t("researcherAccess.autoGrant")}
              </option>
            </select>
          </SettingRow>

          <SettingRow
            title={t("catalogVisibility.title")}
            description={t("catalogVisibility.description")}
          >
            <select
              value={settings.default_metadata_scope}
              onChange={(e) =>
                patch({
                  default_metadata_scope: e.target.value as MetadataScope,
                })
              }
              disabled={readOnly}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="projects">{t("catalogVisibility.projects")}</option>
              <option value="org">{t("catalogVisibility.org")}</option>
              <option value="private">{t("catalogVisibility.hidden")}</option>
            </select>
          </SettingRow>

          <SettingRow
            title={t("autoNotify.title")}
            description={t("autoNotify.description")}
          >
            <Toggle
              checked={settings.auto_notify_on_upload}
              onChange={(v) => patch({ auto_notify_on_upload: v })}
              disabled={saving || readOnly}
            />
          </SettingRow>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm">
            {error && <span className="text-red-600">{error}</span>}
            {successMsg && <span className="text-green-600">{successMsg}</span>}
          </div>
          <button
            onClick={handleSave}
            disabled={saving || readOnly}
            className="btn btn-primary"
          >
            {saving ? t("settings.saving") : t("settings.saveButton")}
          </button>
        </div>
      </div>

      {/* Project controls*/}
      {(canArchive || canLeave) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {t("controls.title")}
            </h3>
          </div>

          <div className="px-6">
            {canArchive && (
              <SettingRow
                title={
                  isArchived
                    ? t("archive.restoreTitle")
                    : t("archive.archiveTitle")
                }
                description={
                  isArchived
                    ? t("archive.restoreDescription")
                    : t("archive.archiveDescription")
                }
              >
                {isArchived ? (
                  <button
                    onClick={() => onSetArchived?.(false)}
                    disabled={archiving}
                    className="btn btn-secondary"
                  >
                    {archiving
                      ? t("archive.restoring")
                      : t("archive.restoreButton")}
                  </button>
                ) : confirmingArchive ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setConfirmingArchive(false)}
                      disabled={archiving}
                      className="btn btn-secondary"
                    >
                      {t("common:actions.cancel")}
                    </button>
                    <button
                      onClick={() => onSetArchived?.(true)}
                      disabled={archiving}
                      className="btn btn-primary"
                    >
                      {archiving
                        ? t("archive.archiving")
                        : t("archive.confirmArchive")}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmingArchive(true)}
                    className="btn btn-secondary"
                  >
                    {t("archive.archiveButton")}
                  </button>
                )}
              </SettingRow>
            )}

            {canLeave && (
              <SettingRow
                title={t("leave.title")}
                description={
                  isOnlyPI
                    ? t("leave.onlyPiDescription")
                    : t("leave.description")
                }
              >
                {confirmingLeave ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setConfirmingLeave(false)}
                      disabled={leaving}
                      className="btn btn-secondary"
                    >
                      {t("common:actions.cancel")}
                    </button>
                    <button
                      onClick={() => onLeaveProject?.()}
                      disabled={leaving}
                      className="btn btn-danger"
                    >
                      {leaving ? t("leave.leaving") : t("leave.confirmLeave")}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmingLeave(true)}
                    disabled={isOnlyPI}
                    title={isOnlyPI ? t("leave.onlyPiHint") : undefined}
                    className="btn btn-danger"
                  >
                    {t("leave.leaveButton")}
                  </button>
                )}
              </SettingRow>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
